// Offer choice on the collar and leash pages: the product alone, or with its matching partner (the Walk Set).
// Follows the native variant form and never changes it. The set is added as two real variants with the
// same _walk_set property the Walk Set builder uses, so the cart applies the same saving.
if (!customElements.get('b2c-set-choice')) {
  customElements.define('b2c-set-choice', class extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('.b2c-product');
      this.fr = this.dataset.lang === 'fr';
      this.de = this.dataset.lang === 'de';
      this.kind = this.dataset.kind === 'leash' ? 'leash' : 'collar';
      this.partnerKind = this.kind === 'leash' ? 'collar' : 'leash';
      try { this.catalog = JSON.parse(this.querySelector('[data-offer-catalog]').textContent); } catch { return; }
      this.buy = this.root?.querySelector('block-buy-buttons');
      this.form = this.buy?.querySelector('form');
      this.idInput = this.form?.querySelector('input[name="id"]');
      this.button = this.form?.querySelector('[name="add"]');
      if (!this.root || !this.form || !this.idInput || !this.button) return;
      this.optionChosen = false;
      this.pending = false;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.addEventListener('change', event => {
        if (event.target.matches('[data-partner-option]')) this.optionChosen = true;
        this.update();
      }, {signal});
      // The native picker updates the hidden variant input and fires change on it.
      this.root.addEventListener('change', event => {
        if (event.target.matches('input[name="id"]')) this.update();
      }, {signal});
      // The size gate (astra-b2c-preview.js) registers before this and stops the submit while no size is chosen.
      this.root.addEventListener('submit', event => this.onSubmit(event), {capture: true, signal});
      // Leash page: only the Standard width is sold (17 Oct 2026, Luuk); the width choice is hidden in CSS,
      // so the native picker is moved to Standard once and fires its own change.
      if (this.kind === 'leash') {
        // Small and Standard cost the same, so only the variant id changes: no picker re-render or section request.
        const current = this.own();
        const list = this.catalog.self || this.catalog[this.kind];
        const standardVariant = current && !/^standard$/i.test(String(current.size)) ? list.find(variant => variant.colour === current.colour && /^standard$/i.test(String(variant.size))) : null;
        if (standardVariant) {
          const radio = [...this.root.querySelectorAll('fieldset input[type="radio"]')].find(input => /^standard$/i.test(input.value) && !input.closest('b2c-set-choice'));
          if (radio) radio.checked = true;
          this.idInput.value = standardVariant.id;
          this.idInput.dispatchEvent(new Event('change', {bubbles: true}));
        }
      }
      this.update();
    }
    disconnectedCallback() { this.controller?.abort(); }
    offer() { return this.querySelector('[data-offer-choice]:checked')?.value || this.kind; }
    own() {
      const id = Number(this.idInput.value);
      return (this.catalog.self || this.catalog[this.kind]).find(variant => variant.id === id) || null;
    }
    // On a colour product the form sells that product's variant; the set is built from the main product's
    // matching variant, the only one the cart discount applies to.
    mainOwn(own) {
      if (!own) return null;
      if (!this.catalog.self) return own;
      return this.catalog[this.kind].find(variant => variant.colour === own.colour && String(variant.size).toLowerCase() === String(own.size).toLowerCase()) || null;
    }
    partnerOption(own) {
      const inputs = [...this.querySelectorAll('[data-partner-option]')];
      const checked = inputs.find(input => input.checked);
      if (checked && (this.optionChosen || this.kind === 'leash')) return checked.value;
      // Leash page: the collar size is a fit decision, so nothing is preselected.
      if (this.kind === 'leash') return null;
      // Collar page: always the Standard leash (17 Oct 2026, Luuk: the Small leash is no longer sold on the webshop).
      const wanted = inputs.find(input => /^standard$/i.test(input.value)) || inputs[0];
      if (wanted && !wanted.checked) wanted.checked = true;
      return wanted?.value || null;
    }
    partner(own) {
      if (!own) return null;
      const option = this.partnerOption(own);
      if (!option) return null;
      return this.catalog[this.partnerKind].find(variant => variant.colour === own.colour && String(variant.size).toLowerCase() === option.toLowerCase()) || null;
    }
    saving(leashPrice) {
      const discount = this.catalog.discount || {};
      if (!discount.enabled) return 0;
      return discount.type === 'amount' ? Math.min(discount.value, leashPrice) : Math.floor(leashPrice * discount.value);
    }
    money(cents) {
      // Derive the store's money format from a rendered sample (123456 → "$1,234.56" / "1.234,56 €").
      const sample = String(this.catalog.moneySample || '$1,234.56');
      const match = sample.match(/^(\D*)1(\D?)234(\D?)(56)?(\D*)$/);
      const prefix = match ? match[1] : '$', thousands = match ? match[2] : ',', decimal = match ? match[3] : '.';
      const decimals = match ? !!match[4] : true, suffix = match ? match[5] : '';
      const whole = String(Math.floor(cents / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return prefix + whole + (decimals ? decimal + String(cents % 100).padStart(2, '0') : '') + suffix;
    }
    update() {
      const own = this.own();
      const partner = this.partner(own);
      const offer = this.offer();
      const ownPrice = own?.price ?? this.catalog[this.kind][0]?.price ?? 0;
      const partnerPrice = partner?.price ?? this.catalog[this.partnerKind][0]?.price ?? 0;
      const leashPrice = this.kind === 'leash' ? ownPrice : partnerPrice;
      const separate = ownPrice + partnerPrice;
      const total = separate - this.saving(leashPrice);
      this.querySelector('[data-offer-price="own"]').textContent = this.money(ownPrice);
      this.querySelector('[data-offer-price="set"]').textContent = this.money(total);
      this.querySelector('[data-offer-separate]').textContent = this.money(separate);
      const badge = this.querySelector('[data-offer-badge]');
      if (badge) {
        badge.hidden = separate - total <= 0;
        badge.textContent = (this.fr ? 'Économisez ' : this.de ? 'Spare ' : 'Save ') + this.money(separate - total);
      }
      this.querySelector('[data-offer-partner]').hidden = offer !== 'set';
      // With the set chosen, the button asks for the collar size until one is picked (leash page only).
      this.root.dataset.offerReady = String(offer !== 'set' || !!this.partnerOption(own));
      const stock = this.querySelector('[data-offer-stock]');
      if (offer === 'set' && own && this.partnerOption(own)) {
        const available = !!(this.mainOwn(own)?.available && partner?.available);
        stock.dataset.available = String(available);
        stock.textContent = available
          ? (this.fr ? `Collier ${own.colour} + laisse ${own.colour} · les deux en stock` : this.de ? `Halsband ${own.colour} + Leine ${own.colour} · beide auf Lager` : `${own.colour} collar + ${own.colour} leash · both in stock`)
          : (this.fr ? 'Cette combinaison n’est pas disponible pour le moment.' : this.de ? 'Diese Kombination ist gerade nicht verfügbar.' : 'This combination is not available right now.');
      } else {
        stock.textContent = '';
      }
      this.root.dataset.offer = offer;
      const price = offer === 'set' ? total : ownPrice;
      this.root.style.setProperty('--b2c-price-label', `'${this.money(price).replace(/'/g, '')}'`);
      const sticky = this.root.querySelector('[data-sticky-price]');
      if (sticky) sticky.textContent = this.money(price);
      const shipping = this.root.querySelector('[data-trust-shipping]');
      const threshold = Number(this.root.querySelector('[data-trust-line]')?.dataset.threshold || 0);
      if (shipping && threshold > 0) shipping.textContent = offer === 'set' && price >= threshold ? shipping.dataset.textFree : shipping.dataset.textFrom;
      this.error(null);
    }
    error(message) {
      const element = this.querySelector('[data-offer-error]');
      if (!element) return;
      element.hidden = !message;
      element.textContent = message || '';
    }
    async onSubmit(event) {
      if (this.offer() !== 'set' || !event.target.matches('block-buy-buttons form') || this.pending) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const own = this.own();
      if (!this.partnerOption(own)) {
        this.error(this.fr ? 'Choisissez la taille du collier pour compléter le set.' : this.de ? 'Wähle die Halsbandgröße, um das Set zu vervollständigen.' : 'Choose the collar size to complete the set.');
        this.querySelector('[data-partner-option]')?.focus();
        return;
      }
      const partner = this.partner(own);
      const main = this.mainOwn(own);
      if (!own || !main || !partner || !main.available || !partner.available) {
        this.error(this.fr ? 'Cette combinaison n’est pas disponible. Choisissez une autre option ou composez votre set.' : this.de ? 'Diese Kombination ist nicht verfügbar. Wähle eine andere Option oder stell dein eigenes Set zusammen.' : 'This combination is not available. Choose another option or mix your own set.');
        return;
      }
      const collar = this.kind === 'collar' ? main : partner;
      const leash = this.kind === 'leash' ? main : partner;
      const properties = {_walk_set: `${collar.id}:${leash.id}`};
      const items = [collar, leash].map(variant => ({id: variant.id, quantity: 1, properties}));
      const route = window.Shopify?.routes?.root || '/';
      const wasDisabled = this.button.disabled;
      this.pending = true;
      this.button.setAttribute('aria-busy', 'true');
      this.button.disabled = true;
      let added = false;
      try {
        const response = await fetch(`${route}cart/add.js`, {
          method: 'POST', credentials: 'same-origin',
          headers: {'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest'},
          body: JSON.stringify({items, sections: ['cart-ajax'], sections_url: window.location.pathname})
        });
        const cart = await response.json();
        if (!response.ok) {
          const message = typeof cart.description === 'string' ? cart.description : cart.message;
          throw new Error(message || (this.fr ? 'Vérifiez la disponibilité de vos articles.' : this.de ? 'Bitte prüfe die Verfügbarkeit deiner Artikel.' : 'Please check the availability of your items.'));
        }
        added = true;
        // Once Shopify accepts the add, a drawer failure must never invite a duplicate add.
        if (this.buy.dataset.cartType === 'page' || !cart.sections?.['cart-ajax']) {
          window.location.assign(`${route}cart`);
        } else {
          this.buy.dispatchEvent(new CustomEvent('ajaxProduct:added', {bubbles: true, detail: {product: cart, addToCartBtn: this.button}}));
        }
      } catch (failure) {
        if (added) {
          window.location.assign(`${route}cart`);
        } else {
          this.error(failure instanceof TypeError || failure instanceof SyntaxError
            ? (this.fr ? 'Impossible de confirmer l’ajout. Vérifiez votre panier avant de réessayer.' : this.de ? 'Wir konnten das Hinzufügen nicht bestätigen. Prüfe deinen Warenkorb, bevor du es erneut versuchst.' : 'We could not confirm the add. Check your cart before trying again.')
            : failure.message);
        }
      } finally {
        this.pending = false;
        this.button.removeAttribute('aria-busy');
        this.button.disabled = wasDisabled;
      }
    }
  });
}
